/**
 * Sentry instrumentation for BeeWeddy React frontend.
 *
 * Imported as the **first** import in `index.js` so global error handlers
 * are wired before any application code runs.
 *
 * Disabled silently when the DSN is missing or `REACT_APP_SENTRY_ENABLED` is
 * not `true`. The wedding marketplace is low-volume so we capture all
 * errors, sample 20% of traces, and capture every error-session replay.
 *
 * Environment tag (`production` / `preview` / `local`) is read from
 * `REACT_APP_SENTRY_ENVIRONMENT` and shown as a filter chip in the Sentry
 * UI so admin can separate beeweddy.com noise from preview noise.
 */
import * as Sentry from '@sentry/react';

const DSN = process.env.REACT_APP_SENTRY_DSN;
const ENV = process.env.REACT_APP_SENTRY_ENVIRONMENT || 'preview';
const ENABLED = process.env.REACT_APP_SENTRY_ENABLED === 'true';

// Sample-rate knobs (override in the .env if traffic grows).
const TRACES_RATE = Number(process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || '0.2');
const REPLAY_RATE = Number(process.env.REACT_APP_SENTRY_REPLAYS_SESSION_SAMPLE_RATE || '0.1');
const REPLAY_ON_ERROR_RATE = Number(process.env.REACT_APP_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE || '1.0');

// Defensive PII scrubbing — strip phone numbers / OTPs from any free-text
// message that gets to Sentry. The backend also scrubs, but redundant
// belt-and-suspenders defense is cheap and our marketplace handles a lot
// of mobile numbers.
const PHONE_RE = /(?:\+?91)?[ -]?[6-9]\d{9}/g;
const OTP_RE = /\b\d{4,6}\b/g;
const SENSITIVE_KEYS = new Set([
  'phone', 'phone_number', 'otp', 'password', 'token', 'access_token',
  'refresh_token', 'authorization', 'cookie',
]);

const scrub = (node) => {
  if (node == null) return node;
  if (Array.isArray(node)) return node.map(scrub);
  if (typeof node === 'object') {
    const out = {};
    for (const k of Object.keys(node)) {
      out[k] = SENSITIVE_KEYS.has(k.toLowerCase()) ? '[redacted]' : scrub(node[k]);
    }
    return out;
  }
  if (typeof node === 'string') {
    return node.replace(PHONE_RE, '[redacted-phone]').replace(OTP_RE, '[redacted-otp]');
  }
  return node;
};

if (DSN && ENABLED) {
  Sentry.init({
    dsn: DSN,
    environment: ENV,
    sendDefaultPii: false,
    tracesSampleRate: TRACES_RATE,
    replaysSessionSampleRate: REPLAY_RATE,
    replaysOnErrorSampleRate: REPLAY_ON_ERROR_RATE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Block all user-typed input from replays so we don't ship phone
        // numbers, OTPs, or addresses to Sentry's servers.
        maskAllInputs: true,
        maskAllText: false, // text labels are useful for debugging
        blockAllMedia: false,
      }),
    ],
    beforeSend(event) {
      try { return scrub(event); } catch (_) { return event; }
    },
    beforeBreadcrumb(breadcrumb) {
      try { return scrub(breadcrumb); } catch (_) { return breadcrumb; }
    },
  });
}

export const SentryErrorBoundary = Sentry.ErrorBoundary;
export const isSentryEnabled = Boolean(DSN && ENABLED);
export default Sentry;
